import { useEffect, useMemo, useState } from 'react'
import { db } from '../lib/firebase'
import { Link, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom'
import { where, setDoc, doc } from 'firebase/firestore'
import { useAuth } from '../lib/auth'
import { useCollection, useDocument } from '../lib/firestore'
import { useTourAccess } from '../lib/tours'
import { dayKey, formatDay, formatDayLong } from '../lib/format'
import { useAirports } from '../lib/airports'
import { toDateInputValue } from '../lib/zonedTime'
import type { Flight, Hotel, ItineraryItem, Show, Tour, TourCrew, WithId } from '../types/models'
import Icon from '../components/Icon'
import TourHero, { heroButtonStyle } from '../components/TourHero'
import ShareSheet from '../components/ShareSheet'
import DateScroller from '../components/DateScroller'
import type { DayEntry } from '../components/DateScroller'
import MonthCalendar from '../components/MonthCalendar'
import ItineraryCard from '../components/ItineraryCard'
import FlightCard from '../components/FlightCard'
import DeleteConfirm from '../components/DeleteConfirm'
import { HotelCard } from '../components/Cards'
import Empty from '../components/Empty'
import { deleteFlight, deleteHotel, deleteItineraryItem, deleteShow } from '../lib/mutations'
import TourDialog from '../dialogs/TourDialog'
import ShowDialog from '../dialogs/ShowDialog'
import ItineraryDialog from '../dialogs/ItineraryDialog'
import { HotelDialog } from '../dialogs/TravelDialogs'
import FlightDialog from '../dialogs/FlightDialog'
import OverviewTab from './tour/OverviewTab'
import CrewTab from './tour/CrewTab'
import RoutePlanner from '../components/RoutePlanner'
import RouteBuilder from '../components/RouteBuilder'

type Tab = 'overview' | 'schedule' | 'shows' | 'travel' | 'crew'

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'shows', label: 'Shows' },
  { id: 'travel', label: 'Travel' },
  { id: 'crew', label: 'Crew' },
]

export default function TourDetail() {
  const { tourId } = useParams<{ tourId: string }>()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { uid } = useAuth()
  const { data: tour, loading } = useDocument<Tour>('tours', tourId)
  const access = useTourAccess(tour, uid)
  const [editingTour, setEditingTour] = useState(false)
  const [sharing, setSharing] = useState(false)

  // Read from the path, not from a `:tab` param.
  //
  // Four of the five tabs have their own route — `tours/:tourId/shows`,
  // `/schedule`, `/travel`, `/crew` — because mobile renders different
  // components for them. React Router ranks a static segment above a dynamic
  // one, so those routes match before `tours/:tourId/:tab` and `params.tab`
  // comes back UNDEFINED on exactly the tabs that matter. Every tab silently
  // fell back to Overview, which is why clicking them did nothing.
  const segment = pathname.split('/').filter(Boolean).pop()
  const active: Tab = TABS.some((entry) => entry.id === segment)
    ? (segment as Tab)
    : 'overview'
  const canEdit = access?.canEdit ?? false

  if (loading) return <div style={{ padding: 30 }} />

  if (!tour) {
    return (
      <div style={{ padding: 30 }}>
        <Empty
          title="Tour not found"
          message="It may have been deleted, or you no longer have access."
        />
      </div>
    )
  }

  if (access && access.state.kind !== 'open') {
    const gate = access.state
    return (
      <div style={{ padding: 30 }}>
        <div className="panel panel-pad stack" style={{ alignItems: 'center', padding: 46 }}>
          <span className="t-eyebrow">Access</span>
          <h2 className="t-display-s">
            {gate.kind === 'pending' ? 'Not open yet' : 'Access ended'}
          </h2>
          <p className="t-caption">
            {gate.kind === 'pending'
              ? `Your access to ${tour.tourName} opens ${formatDayLong(gate.startsOn)}.`
              : `Your access to ${tour.tourName} ended ${formatDayLong(gate.endedOn)}.`}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ paddingBottom: 72 }}>
      {/* Outside the padded column so the artwork runs edge to edge. */}
      <TourHero
        artist={tour.artist}
        tourName={tour.tourName}
        posterURL={tour.posterURL}
        canEdit={canEdit}
        onEdit={() => setEditingTour(true)}
        framing={{
          scale: tour.posterScale ?? 1,
          offsetX: tour.posterOffsetX ?? 0,
          offsetY: tour.posterOffsetY ?? 0,
        }}
        onSaveFraming={(framing) =>
          setDoc(
            doc(db, 'tours', tour.id),
            {
              posterScale: framing.scale,
              posterOffsetX: framing.offsetX,
              posterOffsetY: framing.offsetY,
            },
            { merge: true },
          )
        }
        actions={
          <button className="row" style={heroButtonStyle} onClick={() => setSharing(true)}>
            Share
            <Icon name="share" size={15} />
          </button>
        }
      />

      <ShareSheet
        open={sharing}
        onClose={() => setSharing(false)}
        title={tour.tourName}
        subtitle={tour.artist}
        url={`/tours/${tour.id}`}
        tourId={tour.id}
      />

      <div
        className="sections"
        style={{ maxWidth: 1440, margin: '0 auto', padding: '0 var(--gutter)', gap: 34 }}
      >
        <TourStats tour={tour} />

        <nav
          className="row scroll-x"
          style={{ gap: 4, borderBottom: '1px solid var(--hairline)' }}
        >
          {TABS.map((entry) => (
            <NavLink
              key={entry.id}
              to={`/tours/${tour.id}/${entry.id}`}
              style={tabStyle(entry.id === active)}
            >
              {entry.label}
            </NavLink>
          ))}
        </nav>

        {active === 'overview' && <OverviewTab tour={tour} canEdit={canEdit} />}
        {active === 'schedule' && <ScheduleTab tour={tour} canEdit={canEdit} />}
        {active === 'shows' && <ShowsTab tour={tour} canEdit={canEdit} />}
        {active === 'travel' && <TravelTab tour={tour} canEdit={canEdit} />}
        {active === 'crew' && (
          <CrewTab tour={tour} canManage={access?.canManageCrew ?? false} />
        )}
      </div>

      {editingTour && uid && (
        <TourDialog
          open
          tour={tour}
          ownerId={uid}
          onClose={() => setEditingTour(false)}
          onDeleted={() => navigate('/tours', { replace: true })}
        />
      )}
    </div>
  )
}

function tabStyle(isActive: boolean): React.CSSProperties {
  return {
    padding: '10px 4px',
    marginRight: 22,
    fontSize: '1rem',
    fontWeight: isActive ? 700 : 500,
    color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
    boxShadow: isActive ? 'inset 0 -2px 0 0 var(--accent)' : 'none',
  }
}

/* ------------------------------------------------------------------ stats */

function TourStats({ tour }: { tour: WithId<Tour> }) {
  const shows = useCollection<Show>('shows', [where('tourId', '==', tour.id)])
  const crew = useCollection<TourCrew>('tourCrew', [where('tourId', '==', tour.id)])

  const manager = crew.data.find((member) => member.roles?.includes('Tour Manager'))
  const started = tour.startDate.toDate() <= new Date()

  return (
    <div
      className="responsive-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 18,
        marginTop: 26,
      }}
    >
      <StatCard value={manager?.name ?? 'Not assigned'} label="Tour manager" />
      <StatCard
        value={String(daysUntil(tour))}
        label={started ? 'Days left' : 'Days until tour'}
        emphasised
      />
      <StatCard value={String(shows.data.length)} label="Total shows" />
    </div>
  )
}

function StatCard({
  value,
  label,
  emphasised,
}: {
  value: string
  label: string
  emphasised?: boolean
}) {
  return (
    <div className="panel stack" style={{ gap: 6, padding: '20px 22px' }}>
      <span
        className="t-display-m truncate"
        style={{ color: emphasised ? 'var(--accent-text)' : undefined }}
        title={value}
      >
        {value}
      </span>
      <span className="t-eyebrow">{label}</span>
    </div>
  )
}

function daysUntil(tour: WithId<Tour>): number {
  const day = 86_400_000
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const start = tour.startDate.toDate()
  start.setHours(0, 0, 0, 0)
  const end = tour.endDate.toDate()
  end.setHours(0, 0, 0, 0)
  if (today < start) return Math.round((start.getTime() - today.getTime()) / day)
  return Math.max(0, Math.round((end.getTime() - today.getTime()) / day))
}

/* --------------------------------------------------------------- schedule */

/**
 * The full itinerary, full width.
 *
 * Overview carries the summary and the side rail; this page has one job, so it
 * gives the schedule the whole column — a day sheet is read across, and 500pt
 * of crew list next to it was taking room the running order wanted.
 */
function ScheduleTab({ tour, canEdit }: { tour: WithId<Tour>; canEdit: boolean }) {
  const { uid } = useAuth()
  const items = useCollection<ItineraryItem>('itineraryItems', [where('tourId', '==', tour.id)])
  const shows = useCollection<Show>('shows', [where('tourId', '==', tour.id)])
  // The itinerary read `itineraryItems` alone, so a tour with four flights on
  // it showed none of them on the days they leave — the one page you open to
  // find out what happens on a travel day.
  const flights = useCollection<Flight>('flights', [where('tourId', '==', tour.id)])
  const crew = useCollection<TourCrew>('tourCrew', [where('tourId', '==', tour.id)])

  const [mode, setMode] = useState<'list' | 'calendar'>('list')
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [deleting, setDeleting] = useState<WithId<ItineraryItem> | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)

  // Grouped by the item's OWN timezone day — a 16 Nov Melbourne arrival must
  // not land on 17 Nov for a viewer in New Zealand.
  const { days, byDay, byDayFlights, showCities, showDayKeys } = useMemo(() => {
    // Flights first, so an itinerary row written for the same departure can be
    // dropped rather than shown twice. Matched within an hour because a
    // hand-written "Fly to Brisbane" rarely carries the exact scheduled time.
    const byDayFlights = new Map<string, WithId<Flight>[]>()
    for (const flight of flights.data) {
      const key = dayKey(flight.departureTimeUTC.toDate(), null)
      const bucket = byDayFlights.get(key)
      if (bucket) bucket.push(flight)
      else byDayFlights.set(key, [flight])
    }
    for (const bucket of byDayFlights.values()) {
      bucket.sort(
        (a, b) => a.departureTimeUTC.toMillis() - b.departureTimeUTC.toMillis(),
      )
    }

    const HOUR = 60 * 60 * 1000
    const shadowed = (item: WithId<ItineraryItem>) =>
      item.type === 'flight' &&
      flights.data.some(
        (flight) =>
          Math.abs(flight.departureTimeUTC.toMillis() - item.timeUTC.toMillis()) < HOUR,
      )

    const byDay = new Map<string, WithId<ItineraryItem>[]>()
    for (const item of items.data) {
      if (shadowed(item)) continue
      const key = dayKey(item.timeUTC.toDate(), item.timezone)
      const bucket = byDay.get(key)
      if (bucket) bucket.push(item)
      else byDay.set(key, [item])
    }
    for (const bucket of byDay.values()) {
      bucket.sort((a, b) => a.timeUTC.toMillis() - b.timeUTC.toMillis())
    }

    const showCities = new Map<string, string>()
    const showDayKeys = new Set<string>()
    for (const show of shows.data) {
      const key = dayKey(show.date.toDate(), show.timezone)
      showCities.set(key, show.city)
      showDayKeys.add(key)
    }

    const days: DayEntry[] = Array.from(
      new Set([...byDay.keys(), ...byDayFlights.keys()]),
    )
      .sort()
      .map((key) => ({
        key,
        date: dateFromKey(key),
        city: showCities.get(key) ?? null,
        count: (byDay.get(key)?.length ?? 0) + (byDayFlights.get(key)?.length ?? 0),
      }))

    return { days, byDay, byDayFlights, showCities, showDayKeys }
  }, [items.data, shows.data, flights.data])

  // Open on today, or the next day with anything on it.
  const resolvedKey = useMemo(() => {
    if (selectedKey) return selectedKey
    if (days.length === 0) return null
    const todayKey = dayKey(new Date())
    return (days.find((day) => day.key >= todayKey) ?? days[0]).key
  }, [selectedKey, days])

  const dayItems = resolvedKey ? (byDay.get(resolvedKey) ?? []) : []
  const dayFlights = resolvedKey ? (byDayFlights.get(resolvedKey) ?? []) : []

  const counts = useMemo(() => {
    const map = new Map<string, number>()
    for (const [key, bucket] of byDay) map.set(key, bucket.length)
    return map
  }, [byDay])

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="spread">
        <h2 style={{ fontSize: '1.05rem', fontWeight: 700 }}>Itinerary</h2>
        <div className="row" style={{ gap: 8 }}>
          <span className="t-caption" style={{ marginRight: 4 }}>
            {items.data.length} item{items.data.length === 1 ? '' : 's'} · {days.length} day
            {days.length === 1 ? '' : 's'}
          </span>
          <ModeToggle mode={mode} onChange={setMode} />
          {canEdit && (
            <button className="btn btn-primary" onClick={() => setAdding(true)}>
              <Icon name="plus" size={16} /> Add item
            </button>
          )}
        </div>
      </div>

      {items.data.length === 0 ? (
        <Empty
          title="Nothing scheduled"
          message="Add flights, hotels and shows and they'll build the itinerary."
          action={canEdit ? { label: 'Add the first item', onClick: () => setAdding(true) } : undefined}
        />
      ) : (
        <>
          {mode === 'list' ? (
            <DateScroller days={days} selectedKey={resolvedKey} onSelect={setSelectedKey} />
          ) : (
            <MonthCalendar
              counts={counts}
              showDays={showDayKeys}
              selectedKey={resolvedKey}
              onSelect={setSelectedKey}
              initialMonth={resolvedKey ? dateFromKey(resolvedKey) : new Date()}
            />
          )}

          <DayHeading
            dayKey={resolvedKey}
            city={resolvedKey ? showCities.get(resolvedKey) : null}
            count={dayItems.length + dayFlights.length}
          />

          {dayItems.length === 0 && dayFlights.length === 0 ? (
            <p className="panel panel-pad t-caption">Nothing scheduled on this day.</p>
          ) : (
            <div className="stack" style={{ gap: 10 }}>
              {/* A travel day is the one day the schedule isn't what you need
                  — the flight is. Same card the Travel tab uses, so the
                  passengers, seats and baggage read identically. */}
              {dayFlights.map((flight) => (
                <FlightCard
                  key={flight.id}
                  flight={flight}
                  crew={crew.data}
                  // Read-only here. The itinerary is where you check what is
                  // happening; the Travel tab is where flights are managed,
                  // and two places to edit the same record is two places for
                  // them to disagree.
                  canEdit={false}
                  onEdit={() => {}}
                  onDelete={() => {}}
                />
              ))}
              {dayItems.map((item) => (
                <ItineraryCard
                  key={item.id}
                  item={item}
                  tourId={tour.id}
                  userId={uid ?? ''}
                  open={openId === item.id}
                  onToggle={() => setOpenId(openId === item.id ? null : item.id)}
                  canEdit={canEdit}
                  onDelete={() => setDeleting(item)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {adding && uid && (
        <ItineraryDialog
          open
          tourId={tour.id}
          userId={uid}
          defaultDate={resolvedKey ?? toDateInputValue(new Date())}
          onClose={() => setAdding(false)}
        />
      )}

      {deleting && (
        <DeleteConfirm
          label={`“${deleting.title}”`}
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await deleteItineraryItem(deleting.id)
            setDeleting(null)
          }}
        />
      )}
    </div>
  )
}

function DayHeading({
  dayKey: key,
  city,
  count,
}: {
  dayKey: string | null
  city?: string | null
  count: number
}) {
  if (!key) return null
  const date = dateFromKey(key)
  return (
    <div className="spread" style={{ alignItems: 'flex-end', paddingTop: 4 }}>
      <div className="stack" style={{ gap: 1 }}>
        {city ? (
          <>
            <span className="t-display-m">{city}</span>
            <span className="t-spec-caption dim">{formatDayLong(date)}</span>
          </>
        ) : (
          <span className="t-display-s">{formatDayLong(date)}</span>
        )}
      </div>
      <span className="t-caption">
        {count} item{count === 1 ? '' : 's'}
      </span>
    </div>
  )
}


function ModeToggle({
  mode,
  onChange,
}: {
  mode: 'list' | 'calendar'
  onChange: (next: 'list' | 'calendar') => void
}) {
  return (
    <div
      role="group"
      aria-label="Itinerary view"
      style={{
        display: 'flex',
        gap: 2,
        padding: 3,
        background: 'var(--panel-raised)',
        borderRadius: 'var(--radius-control)',
      }}
    >
      {(
        [
          { value: 'list', icon: 'list', label: 'List' },
          { value: 'calendar', icon: 'calendar', label: 'Calendar' },
        ] as const
      ).map((option) => {
        const selected = mode === option.value
        return (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            aria-label={option.label}
            aria-pressed={selected}
            title={option.label}
            style={{
              display: 'grid',
              placeItems: 'center',
              width: 34,
              height: 30,
              borderRadius: 7,
              background: selected ? 'var(--panel)' : 'transparent',
              color: selected ? 'var(--text-primary)' : 'var(--text-tertiary)',
            }}
          >
            <Icon name={option.icon} size={15} />
          </button>
        )
      })}
    </div>
  )
}


function dateFromKey(key: string): Date {
  const [year, month, day] = key.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/* ------------------------------------------------------------------ shows */

function ShowsTab({ tour, canEdit }: { tour: WithId<Tour>; canEdit: boolean }) {
  const { uid } = useAuth()
  const shows = useCollection<Show>('shows', [where('tourId', '==', tour.id)])
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<WithId<Show> | null>(null)
  const [deleting, setDeleting] = useState<WithId<Show> | null>(null)

  const sorted = [...shows.data].sort((a, b) => a.date.toMillis() - b.date.toMillis())
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const nextShowId = sorted.find((show) => show.date.toDate() >= today)?.id

  return (
    <div className="stack" style={{ gap: 14 }}>
      <div className="spread">
        <h2 style={{ fontSize: '1.05rem', fontWeight: 700 }}>Shows</h2>
        <span className="t-caption">
          {shows.data.length} show{shows.data.length === 1 ? '' : 's'}
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
          gap: 16,
        }}
      >
        {sorted.map((show) => (
          <Link
            key={show.id}
            to={`/tours/${tour.id}/shows/${show.id}`}
            className="panel stack"
            style={{
              padding: 20,
              gap: 4,
              alignItems: 'center',
              textAlign: 'center',
              minHeight: 130,
              justifyContent: 'center',
              borderColor: show.id === nextShowId ? 'var(--accent)' : undefined,
              borderWidth: show.id === nextShowId ? 'var(--signal-rule)' : undefined,
            }}
          >
            {show.id === nextShowId && <span className="chip chip-accent">Next</span>}
            <span style={{ fontWeight: 700, fontSize: '1.02rem' }}>{show.city}</span>
            <span className="t-caption truncate" style={{ maxWidth: '100%' }}>
              {show.venueName}
            </span>
            <span className="t-spec-caption faint">{formatDay(show.date, show.timezone)}</span>
          </Link>
        ))}

        {canEdit && (
          <button
            onClick={() => setAdding(true)}
            className="panel stack"
            style={{
              padding: 20,
              gap: 10,
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 130,
              borderStyle: 'dashed',
              color: 'var(--text-secondary)',
            }}
          >
            <span
              style={{
                display: 'grid',
                placeItems: 'center',
                width: 34,
                height: 34,
                borderRadius: '50%',
                background: 'var(--accent)',
                color: 'var(--on-accent)',
              }}
            >
              <Icon name="plus" size={18} strokeWidth={2} />
            </span>
            Add another
          </button>
        )}
      </div>

      {shows.data.length === 0 && !canEdit && (
        <Empty title="No shows yet" message="Nothing has been added to this tour." />
      )}


      {deleting && (
        <DeleteConfirm
          label={`${deleting.city} · ${deleting.venueName}`}
          detail="The show's running-order items come off the itinerary too."
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await deleteShow(deleting)
            setDeleting(null)
          }}
        />
      )}

      {(adding || editing) && uid && (
        <ShowDialog
          open
          show={editing}
          tourId={tour.id}
          userId={uid}
          defaultDate={toDateInputValue(tour.startDate.toDate())}
          onClose={() => {
            setAdding(false)
            setEditing(null)
          }}
        />
      )}
    </div>
  )
}

/* ----------------------------------------------------------------- travel */

function TravelTab({ tour, canEdit }: { tour: WithId<Tour>; canEdit: boolean }) {
  const { uid } = useAuth()
  useAirports()
  const flights = useCollection<Flight>('flights', [where('tourId', '==', tour.id)])
  const hotels = useCollection<Hotel>('hotels', [where('tourId', '==', tour.id)])
  const crew = useCollection<TourCrew>('tourCrew', [where('tourId', '==', tour.id)])

  const [addingFlight, setAddingFlight] = useState(false)
  const [editingFlight, setEditingFlight] = useState<WithId<Flight> | null>(null)
  const [deletingFlight, setDeletingFlight] = useState<WithId<Flight> | null>(null)
  const [addingHotel, setAddingHotel] = useState(false)
  const [editingHotel, setEditingHotel] = useState<WithId<Hotel> | null>(null)
  const [deletingHotel, setDeletingHotel] = useState<WithId<Hotel> | null>(null)
  const [buildingRoute, setBuildingRoute] = useState(false)

  return (
    <div className="sections">
      {/* Everything travel-related is added from one control. A flight, a
          hotel and a route are the same decision arriving in three shapes, and
          three separate add buttons made you pick the shape before you'd
          decided the thing. */}
      {canEdit && (
        <div className="spread" style={{ alignItems: 'center' }}>
          <h2 style={{ fontSize: '1.05rem', fontWeight: 700 }}>Travel</h2>
          <AddTravelMenu
            onFlight={() => setAddingFlight(true)}
            onHotel={() => setAddingHotel(true)}
            onRoute={() => setBuildingRoute(true)}
          />
        </div>
      )}

      <section className="section">
        <h2 style={{ fontSize: '1.05rem', fontWeight: 700 }}>Flights</h2>
        {flights.data.length === 0 ? (
          <p className="panel panel-pad t-caption">No flights on this tour.</p>
        ) : (
          <div className="stack">
            {[...flights.data]
              .sort((a, b) => a.departureTimeUTC.toMillis() - b.departureTimeUTC.toMillis())
              .map((flight) => (
                <FlightCard
                  key={flight.id}
                  flight={flight}
                  crew={crew.data}
                  canEdit={canEdit}
                  onEdit={() => setEditingFlight(flight)}
                  onDelete={() => setDeletingFlight(flight)}
                />
              ))}
          </div>
        )}
      </section>

      <section className="section">
        <h2 style={{ fontSize: '1.05rem', fontWeight: 700 }}>Hotels</h2>
        {hotels.data.length === 0 ? (
          <p className="panel panel-pad t-caption">No hotels on this tour.</p>
        ) : (
          <div className="stack">
            {[...hotels.data]
              .sort((a, b) => a.checkInDate.toMillis() - b.checkInDate.toMillis())
              .map((hotel) => (
                <HotelCard
                  key={hotel.id}
                  hotel={hotel}
                  crew={crew.data}
                  canEdit={canEdit}
                  onEdit={() => setEditingHotel(hotel)}
                  onDelete={() => setDeletingHotel(hotel)}
                />
              ))}
          </div>
        )}
      </section>

      {/* Last, because it is a reading of the flights and hotels above rather
          than a plan of its own. */}
      <section className="section">
        <RoutePlanner tour={tour} />
      </section>

      {buildingRoute && (
        <RouteBuilder tour={tour} onClose={() => setBuildingRoute(false)} />
      )}

      {deletingFlight && (
        <DeleteConfirm
          label={`${deletingFlight.origin} → ${deletingFlight.destination}`}
          onCancel={() => setDeletingFlight(null)}
          onConfirm={async () => {
            await deleteFlight(deletingFlight.id)
            setDeletingFlight(null)
          }}
        />
      )}

      {deletingHotel && (
        <DeleteConfirm
          label={deletingHotel.name}
          onCancel={() => setDeletingHotel(null)}
          onConfirm={async () => {
            await deleteHotel(deletingHotel.id)
            setDeletingHotel(null)
          }}
        />
      )}

      {(addingFlight || editingFlight) && uid && (
        <FlightDialog
          open
          flight={editingFlight}
          tourId={tour.id}
          ownerId={uid}
          onClose={() => {
            setAddingFlight(false)
            setEditingFlight(null)
          }}
        />
      )}

      {(addingHotel || editingHotel) && uid && (
        <HotelDialog
          open
          hotel={editingHotel}
          tourId={tour.id}
          ownerId={uid}
          onClose={() => {
            setAddingHotel(false)
            setEditingHotel(null)
          }}
        />
      )}
    </div>
  )
}

/**
 * The one add control on Travel.
 *
 * A flight, a hotel and a route are the same decision arriving in three
 * shapes. Three separate buttons made you choose the shape before you'd
 * decided the thing, and hid the route builder from anyone who hadn't already
 * gone looking for it.
 */
function AddTravelMenu({
  onFlight,
  onHotel,
  onRoute,
}: {
  onFlight: () => void
  onHotel: () => void
  onRoute: () => void
}) {
  const [open, setOpen] = useState(false)

  // Any click outside closes it. A menu that only closes on its own trigger is
  // a menu you end up with two of.
  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    window.addEventListener('pointerdown', close)
    return () => window.removeEventListener('pointerdown', close)
  }, [open])

  const items: Array<{ label: string; hint: string; icon: 'plane' | 'bed' | 'mapPin'; run: () => void }> = [
    { label: 'Route', hint: 'Chain stops together, drive or fly', icon: 'mapPin', run: onRoute },
    { label: 'Flight', hint: 'A single flight with passengers', icon: 'plane', run: onFlight },
    { label: 'Hotel', hint: 'Rooms and who is in them', icon: 'bed', run: onHotel },
  ]

  return (
    <div style={{ position: 'relative' }} onPointerDown={(event) => event.stopPropagation()}>
      <button className="btn btn-primary" onClick={() => setOpen((on) => !on)}>
        <Icon name="plus" size={16} /> Add
      </button>

      {open && (
        <div
          className="panel stack"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 30,
            width: 280,
            padding: 6,
            gap: 2,
            boxShadow: 'var(--shadow-pop)',
          }}
        >
          {items.map((item) => (
            <button
              key={item.label}
              className="row"
              style={{
                gap: 11,
                padding: '10px 11px',
                borderRadius: 9,
                cursor: 'pointer',
                textAlign: 'left',
                background: 'transparent',
                border: 0,
              }}
              onClick={() => {
                setOpen(false)
                item.run()
              }}
            >
              <Icon name={item.icon} size={16} style={{ color: 'var(--text-tertiary)' }} />
              <span className="stack" style={{ gap: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 600 }}>{item.label}</span>
                <span className="t-caption">{item.hint}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
